<script setup>

import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import ResourceService from '@/api/services/resource.service';
import CommonService from '@/api/services/common.service';
import { getYear } from '@/views/production/mixin/common'
import PublicHolidayService from '@/api/services/publicHoliday.service';
import Notification from '@/utils/plugins/NotificationService'

import moment from "moment";
import { onMounted, ref, computed } from "vue";
import { useUiStore } from "@/app/stores/uiStore";
import dayjs from "dayjs";

const checkedWorkplaces = computed({
  get() {
    return workplaceOptions.value.filter(workplace =>
      newHolidayFormModel.value.checkedWorkplaces.some(
        checkedWorkplace => checkedWorkplace.id === workplace.id
      )
    );
  },
  set(newValue) {
    newHolidayFormModel.value.checkedWorkplaces = newValue;
  },
});
const newHolidayForm = ref(null)
const selectedHolidayDateRule = (rule, value, callback) => {
  if (!value) {
    newHolidayFormRules.value.selectedHolidayDate.error = true;
    callback(new Error("Debe especificar una fecha"));
  } else {
    newHolidayFormRules.value.selectedHolidayDate.error = false;
    callback();
  }
}
const selectedYearModalRule = (rule, value, callback) => {
  if (!value) {
    newHolidayFormRules.value.selectedYearModal.error = true;
    callback(new Error("Debe especificar un año"));
  } else {
    newHolidayFormRules.value.selectedYearModal.error = false;
    callback();
  }
}
const selectedTypePublicHolidayRule = (rule, value, callback) => {
  if (!value) {
    newHolidayFormRules.value.selectedTypePublicHoliday.error = true;
    callback(new Error("Debe especificar un tipo"));
  } else {
    newHolidayFormRules.value.selectedTypePublicHoliday.error = false;
    callback();
  }
}
const descriptionModalRule = (rule, value, callback) => {
  if (!value) {
    newHolidayFormRules.value.descriptionModal.error = true;
    callback(new Error("Debe especificar una descripción"));
  } else {
    newHolidayFormRules.value.descriptionModal.error = false;
    callback();
  }
}
const checkedWorkplacesRule = (rule, value, callback) => {
  if (!value || value.length <= 0) {
    newHolidayFormRules.value.checkedWorkplaces.error = true;
    callback(new Error("Por favor selecciona al menos un centro de trabajo"));
  } else {
    newHolidayFormRules.value.checkedWorkplaces.error = false;
    callback();
  }
}
const uiStore = useUiStore();
const selectedYear = ref(new Date().getFullYear())
const yearOptions = ref([])
const serverTimestamp = ref(null)
const currentServerYear = ref(null)
// Selector de centro de trabajo
const selectedWorkplace = ref(null)
const workplaceOptions = ref(null)
// Calendario de festivos
// eslint-disable-next-line no-unused-vars
const publicHolidays = ref([])
const publicHolidaysItemsPerPage = 99999
const params = ref({})
// Modal de nuevo día festivo
// eslint-disable-next-line no-unused-vars
const publicHolidayId = ref(null)
const isUpdateHoliday = ref(false);
const showNewPublicHolidayModal = ref(false)
const newHolidayFormModel = ref({
  publicHolidayId: null,
  selectedHolidayDate: null,
  selectedYearModal: null,
  selectedTypePublicHoliday: null,
  descriptionModal: null,
  checkedWorkplaces: []
})
const newHolidayFormRules = ref({
  selectedHolidayDate: {
    required: true,
    validator: selectedHolidayDateRule,
    error: false,
    trigger: "blur"
  },
  selectedYearModal: {
    required: true,
    validator: selectedYearModalRule,
    error: false,
    trigger: "blur"
  },
  selectedTypePublicHoliday: {
    required: true,
    validator: selectedTypePublicHolidayRule,
    error: false,
    trigger: "blur"
  },
  descriptionModal: {
    required: true,
    validator: descriptionModalRule,
    error: false,
    trigger: "blur"
  },
  checkedWorkplaces: {
    required: true,
    validator: checkedWorkplacesRule,
    error: false,
    trigger: "blur"
  }
})
// Estado de las vacaciones
// eslint-disable-next-line no-unused-vars
const typesPublicHolidays = ref([
  { id: 0, type: 'Nacional' },
  { id: 1, type: 'Local' }
])
// Dialogo de confirmacion al eliminar un festivo
const deleteDialogShown = ref(false)
const selectedPublicHoliday = ref(null)
const deleteMsg = ref('')

onMounted(async () => {
  params.value = {};
  await initialize();
  // getPublicHolidays();
  await uiStore.stopLoading();
})

const initialize = async () => {
  await uiStore.startLoading();
  try {
    const resTimestamp = await CommonService.getServerTimestamp();
    serverTimestamp.value = resTimestamp.data.serverTimestamp;
    currentServerYear.value = getYear(serverTimestamp.value);

    // Rellenamos las opciones del desplegable de 'Año'
    yearOptions.value.push({
      id: parseInt(currentServerYear.value) + 1,
      name: (parseInt(currentServerYear.value) + 1).toString()
    });
    yearOptions.value.push({ id: parseInt(currentServerYear.value), name: currentServerYear.value });
    yearOptions.value.push({
      id: parseInt(currentServerYear.value) - 1,
      name: (parseInt(currentServerYear.value) - 1).toString()
    });

    selectedYear.value = yearOptions.value[1];

    params.value['date[before]'] = moment(new Date(currentServerYear.value + "-12-31")).format('YYYY-MM-DD')
    params.value['date[after]'] = moment(new Date(currentServerYear.value + "-01-01")).format('YYYY-MM-DD')

    try {
      const res = await ResourceService.getWorkplaces({});
      workplaceOptions.value = res.data['hydra:member'];
    } catch {
      Notification.addNotification('Error al obtener los centros de trabajo', 'error', 6000);
    }

    await getPublicHolidays();

    /*
    EmployeeService.getEmployee(employeeID).then((res) => {
        employeeData = res.data;
        title = 'Vacaciones de ' + employeeData.name + ' ' + employeeData.lastName;

        getEmployeeHolidays(employeeID);
    })
    .catch(error => {
        Notification.addNotification('Error al obtener la información del empleado', 'error', 6000);
    });
    */
  } catch {
    Notification.addNotification('Error al obtener el timestamp del servidor', 'error', 6000);
  } finally {
    await uiStore.stopLoading();
  }
}

// eslint-disable-next-line no-unused-vars
const filterByYear = () => {
  if (!selectedYear.value) {
    selectedYear.value = yearOptions.value[1];
  }
  params.value['date[before]'] = moment(new Date(selectedYear.value.name + "-12-31")).format('YYYY-MM-DD')
  params.value['date[after]'] = moment(new Date(selectedYear.value.name + "-01-01")).format('YYYY-MM-DD')

  getPublicHolidays();
}

/* Filtro por centro de trabajo */
// eslint-disable-next-line no-unused-vars
const filterByWorkplace = () => {
  if (selectedWorkplace.value === null && typeof params.value['workplace.id'] !== "undefined") {
    delete params.value['workplace.id'];
  } else {
    params.value['workplace.id'] = selectedWorkplace.value?.id
  }
  getPublicHolidays();
}

/* Obtiene los dias festivos */
const getPublicHolidays = async () => {
  params.value.page = 1
  params.value.itemsPerPage = publicHolidaysItemsPerPage
  await uiStore.startLoading();
  await PublicHolidayService.getPublicHolidays(params.value)
    .then((res) => {
      uiStore.stopLoading();
      publicHolidays.value = res.data['hydra:member'];
    })
    .catch(() => {
      uiStore.stopLoading();
      Notification.addNotification('Error al obtener el listado de dias festivos', 'error', 6000);
    })
}

// eslint-disable-next-line no-unused-vars
const openModalNewPublicHoliday = (publicHoliday = null) => {
  let today = moment(new Date()).format('YYYY-MM-DD')
  newHolidayFormModel.value.selectedYearModal = getYear(today);
  newHolidayFormModel.value.selectedHolidayDate = today;


  if (publicHoliday && publicHoliday.id) {
    newHolidayFormModel.value.publicHolidayId = publicHoliday.id;
    newHolidayFormModel.value.selectedHolidayDate = publicHoliday.date;
    isUpdateHoliday.value = true;
    // TODO: Añadir el año al objeto de festividad año actual    
    newHolidayFormModel.value.selectedYearModal = getYear(publicHoliday.date);
    const findTypePublicHoliday = (typePublicHoliday) => {
      return typePublicHoliday?.type?.toLowerCase() === publicHoliday?.type?.toLowerCase();
    }
    newHolidayFormModel.value.selectedTypePublicHoliday = typesPublicHolidays.value[typesPublicHolidays.value.findIndex(findTypePublicHoliday)];
    newHolidayFormModel.value.descriptionModal = publicHoliday.description;
    newHolidayFormModel.value.checkedWorkplaces.push(publicHoliday.workplace);
  }

  showNewPublicHolidayModal.value = true;
}

const closeModalNewPublicHoliday = () => {
  showNewPublicHolidayModal.value = false;
  isUpdateHoliday.value = false;
  resetVar()
}

const updateDatePicker = () => {
  if (!newHolidayFormModel.value.selectedYearModal) return
  newHolidayFormModel.value.selectedHolidayDate = new Date(newHolidayFormModel.value.selectedYearModal.name + "-01-01")
  return new Date(newHolidayFormModel.value.selectedYearModal.name + "-01-01")
}

const handleTypePublicHolidayChange = (selectedType) => {
  const holidayType = selectedType?.type?.toLowerCase();

  if (holidayType === 'nacional') {
    newHolidayFormModel.value.checkedWorkplaces = [...(workplaceOptions.value || [])];
    newHolidayFormRules.value.checkedWorkplaces.error = false;
    return;
  }

  if (holidayType === 'local') {
    newHolidayFormModel.value.checkedWorkplaces = [];
  }
}

/* Creación y modificación de PublicHoliday */
// eslint-disable-next-line no-unused-vars
const requestNewPublicHoliday = () => {
  newHolidayForm.value.validate(valid => {
    if (valid) {
      const workplace = newHolidayFormModel.value.checkedWorkplaces;
      workplace.forEach(workplace => {
        let publicHoliday = {
          date: dayjs.utc(newHolidayFormModel.value.selectedHolidayDate).tz("Europe/Madrid").format(),
          description: newHolidayFormModel.value.descriptionModal,
          type: (newHolidayFormModel.value.selectedTypePublicHoliday.type).toLowerCase(),
          workplace: workplace['@id']
        }
        if (newHolidayFormModel.value.publicHolidayId) {
          publicHoliday.id = newHolidayFormModel.value.publicHolidayId;
          newHolidayFormModel.value.publicHolidayId = null;
        }

        if (publicHoliday.id) {
          updatePublicHoliday(publicHoliday);
        } else {
          createPublicHoliday(publicHoliday);
        }
      });
      resetVar();
      closeModalNewPublicHoliday();

    } else {
      return false;
    }
  })
}
/* Creacion de un nuevo registro publicHoliday */
const createPublicHoliday = async (publicHoliday) => {
  await uiStore.startLoading();
  await PublicHolidayService.createPublicHoliday(publicHoliday)
    .then(() => {
      uiStore.stopLoading();
      Notification.addNotification('Festivo creado correctamente', 'success', 6000);
      getPublicHolidays();
    })
    .catch(() => {
      Notification.addNotification('Error al crear el festivo', 'error', 6000);
      uiStore.stopLoading();
    });
}

/* Modificación de un registro publicHoliday */
const updatePublicHoliday = async (publicHoliday) => {
  await PublicHolidayService.updatePublicHoliday(publicHoliday, publicHoliday.id)
    .then(() => {
      uiStore.stopLoading();
      Notification.addNotification('Festivo actualizado correctamente', 'success', 6000);
      getPublicHolidays();
    })
    .catch(async () => {
      Notification.addNotification('Error al actualizar el festivo', 'error', 6000);
      uiStore.stopLoading();
    });
}
const resetVar = () => {
  newHolidayFormModel.value.publicHolidayId = null;
  newHolidayFormModel.value.selectedHolidayDate = null;
  newHolidayFormModel.value.selectedYearModal = null;
  newHolidayFormModel.value.selectedTypePublicHoliday = null;
  newHolidayFormModel.value.descriptionModal = null;
  newHolidayFormModel.value.checkedWorkplaces = [];

  newHolidayFormRules.value.selectedHolidayDate.error = false;
  newHolidayFormRules.value.selectedYearModal.error = false;
  newHolidayFormRules.value.selectedTypePublicHoliday.error = false;
  newHolidayFormRules.value.descriptionModal.error = false;
  newHolidayFormRules.value.checkedWorkplaces.error = false;
}
// eslint-disable-next-line no-unused-vars
const deleteDialog = (publicHoliday) => {
  selectedPublicHoliday.value = publicHoliday;
  deleteMsg.value = 'Festividad: \t' + publicHoliday.description + '\n'
    + 'Fecha: \t\t' + moment(publicHoliday.date).format('DD/MM/YYYY') + '\n'
    + 'De: \t\t\t' + publicHoliday.workplace.name;
  deleteDialogShown.value = true;
}
// eslint-disable-next-line no-unused-vars
const deletePublicHoliday = async (publicHoliday) => {
  uiStore.startLoading();
  await PublicHolidayService.deletePublicHoliday(publicHoliday.id)
    .then(() => {
      Notification.addNotification('Festividad eliminada correctamente.', 'success', 6000);
      uiStore.stopLoading();
      deleteDialogShown.value = false;
      resetVar();
      getPublicHolidays();
    })
    .catch(() => {
      Notification.addNotification('Error al intentar eliminar la festividad seleccionado.', 'error', 6000);
      uiStore.stopLoading();
      deleteDialogShown.value = false;
      resetVar();
      getPublicHolidays();
    })
}


const formatDay = (date) => {
  moment.locale('es');
  if (date) {
    return moment(date).format('DD');
  }
  return '';
}
const formatMonthText = (date) => {
  moment.locale('es');
  if (date) {
    return moment(date).format('MMMM');
  }
  return '';
}

</script>

<template>
  <div class="holidays-calendar">
    <HeaderTitle title="Ajustes" />
    <div class="d-flex flex-row justify-content-start">
      <img src="@/assets/icons/group.svg" width="30" alt="">
      <div class="title-text pl-4">
        Calendario de festivos
      </div>
    </div>
    <el-card class="card-search w-100 wback" body-style="padding:0">
      <div class="row mx-5 master-search-row align-items-end">
        <div class="col-lg-8 col-md-8 col-12 container-holidays-calendar">
          <form @submit.prevent>
            <!-- Año y centro de trabajo -->
              <div class="row">
                <div class="col-md-6 col-12 d-inline-flex align-items-center">
                  <div class="form-item">
                    <label class="label-year w-100 mb-2">Año</label>
                    <el-select filterable clearable v-model="selectedYear" class="w-100" :value-key="'id'"
                      placeholder="Selecciona el año" @change="filterByYear">
                      <el-option v-for="year in yearOptions" :key="year.id" :label="year.name" :value="year" />
                    </el-select>
                  </div>
                </div>

                <div class="col-md-6 col-12 d-inline-flex align-items-center">
                  <div class="form-item">
                    <label class="label-work-center w-100 mb-2">Centro de trabajo</label>
                    <el-select filterable clearable v-model="selectedWorkplace" class="w-100"
                      placeholder="Selecciona el centro de trabajo" :value-key="'id'" @change="filterByWorkplace">
                      <el-option v-for="workplace in workplaceOptions" :key="workplace.id" :label="workplace.name"
                        :value="workplace" />
                    </el-select>
                  </div>
                </div>
            </div>
          </form>
        </div>
        <div class="col-lg-4 col-md-4 col-12 d-flex justify-content-end master-search-actions">
          <button class="btn btn_darkpink btn-search holiday-new-button" @click="openModalNewPublicHoliday()">
            Nuevo día festivo
          </button>
        </div>
      </div>
    </el-card>
    <!-- Tabla -->
    <div class="table table-holidays-calendar table-not-mobile">
      <div v-if="publicHolidays && publicHolidays.length > 0">
        <table>
          <thead>
            <tr>
              <th>Centro de trabajo</th>
              <th>Día</th>
              <th>Mes</th>
              <th>Festividad</th>
              <th class="master-actions-col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="publicHoliday in publicHolidays" :key="publicHoliday.id" class="pointer" @click="openModalNewPublicHoliday(publicHoliday)">
              <td>
                {{ publicHoliday.workplace.name }}
              </td>
              <td>
                {{ formatDay(publicHoliday.date) }}
              </td>
              <td>
                {{ formatMonthText(publicHoliday.date) }}
              </td>
              <td>
                {{ publicHoliday.description }}
              </td>
              <td class="master-actions-col">
                <!--                            <div class="buttons d-inline-flex" v-if="user && ifIsInArray('ROLE_SUPER_ADMIN', user.rol)">-->
                <div class="buttons d-inline-flex">
                  <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Editar"
                    @click.stop="openModalNewPublicHoliday(publicHoliday);">
                    <font-awesome-icon icon="circle" style="color: #f7d20e" />
                    <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                  <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Eliminar"
                    @click.stop="deleteDialog(publicHoliday);">
                    <font-awesome-icon icon="circle" style="color:red" />
                    <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <h5>No se han encontrado datos según los filtros especificados...</h5>
      </div>
    </div>

    <div class="table-mobile">
      <div v-if="publicHolidays && publicHolidays.length > 0">
        <el-row v-for="publicHoliday in publicHolidays" :key="publicHoliday.id" class="mt-3">
          <el-card class="master-mobile-card w-100 pointer" @click="openModalNewPublicHoliday(publicHoliday)">
            <div class="master-mobile-card__delete">
              <font-awesome-layers class="fa-2x pointer" title="Eliminar" @click.stop="deleteDialog(publicHoliday)">
                <font-awesome-icon icon="circle" style="color:red" />
                <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
              </font-awesome-layers>
            </div>
            <div class="master-mobile-card__title">{{ publicHoliday.description }}</div>
            <div class="master-mobile-card__item">
              <span><b class="master-mobile-card__label">Centro de trabajo:</b> {{ publicHoliday.workplace.name
              }}</span>
            </div>
            <div class="master-mobile-card__item">
              <span><b class="master-mobile-card__label">Día:</b> {{ formatDay(publicHoliday.date) }}</span>
            </div>
            <div class="master-mobile-card__item">
              <span><b class="master-mobile-card__label">Mes:</b> {{ formatMonthText(publicHoliday.date) }}</span>
            </div>
          </el-card>
        </el-row>
      </div>
      <div v-else class="mt-3">
        <h5>No se han encontrado datos según los filtros especificados...</h5>
      </div>
    </div>

    <!--      <div class="row">-->
    <!--        <div class="col-md-12">-->
    <!--          <div class="d-flex justify-content-end">-->
    <!--            <div class="col-md-2">-->
    <!--              <button-->
    <!--                type="submit"-->
    <!--                class="btn btn_blue new-holiday-button"-->
    <!--                @click="openModalNewPublicHoliday()"-->
    <!--              >-->
    <!--                Nuevo día festivo-->
    <!--              </button>-->
    <!--            </div>-->
    <!--          </div>-->
    <!--        </div>-->
    <!--      </div>-->



    <!-- Dialogo de confirmación de eliminacion de una festividad -->
    <SharedDeleteConfirmDialog v-model="deleteDialogShown" title="Eliminar festividad" :message="deleteMsg"
      @confirm="deletePublicHoliday(selectedPublicHoliday)" @cancel="selectedPublicHoliday = null" />


    <!-- Modal de crear un nuevo festivo -->
    <el-dialog v-model="showNewPublicHolidayModal" class="holiday-calendar-dialog"
      :before-close="closeModalNewPublicHoliday" :show-close="false" size="'medium'" width="750" :center="true"
      :lock-scroll="true" :style="{ borderRadius: `var(--el-border-radius-round)` }">
      <div class="my-header">
        <svg id="asterisk" xmlns="http://www.w3.org/2000/svg" width="724" height="1024" viewBox="0 0 724 1024">
          <path
            d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
        </svg>
        <span class="text-header ml-5 mt-3">{{ isUpdateHoliday ? 'Actualizar ' : 'Nuevo ' }}Día Festivo</span>
        <img class="close-icon pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
          @click="closeModalNewPublicHoliday">
      </div>
      <div class="my-body">
        <el-form ref="newHolidayForm" class="newHolidayForm" :model="newHolidayFormModel" name="newHolidayForm"
          status-icon @submit.prevent>
          <div class="holiday-modal-fields">
            <div class="row item-modal holiday-modal-field">
              <div class="col-sm-3 col-12 d-flex align-items-center">
                <label class="public-holiday-label-modal">Día festivo:</label>
              </div>
              <div class="col-sm-9 col-12">
                <el-form-item prop="selectedHolidayDate" class="holiday-modal-form-item date-picker">
                  <el-date-picker type="date" v-model="newHolidayFormModel.selectedHolidayDate" class="w-100"
                    :class="newHolidayFormRules.selectedHolidayDate.error ? 'error-validation' : ''" />
                </el-form-item>
              </div>
            </div>

            <div class="row item-modal holiday-modal-field">
              <div class="col-sm-3 col-12 d-flex align-items-center">
                <label class="public-holiday-label-modal">Año:</label>
              </div>
              <div class="col-sm-9 col-12">
                <el-form-item prop="selectedYearModal" class="holiday-modal-form-item holiday-modal-control--year">
                  <el-select filterable clearable v-model="newHolidayFormModel.selectedYearModal" placeholder="Todos"
                    class="w-100" @change="updateDatePicker" :value-key="'id'"
                    :class="newHolidayFormRules.selectedYearModal.error ? 'error-validation' : ''">
                    <el-option v-for="year in yearOptions" :key="year.id" :label="year.name" :value="year" />
                  </el-select>
                </el-form-item>
              </div>
            </div>

            <div class="row item-modal holiday-modal-field">
              <div class="col-sm-3 col-12 d-flex align-items-center">
                <label class="public-holiday-label-modal">Festivo:</label>
              </div>
              <div class="col-sm-9 col-12">
                <el-form-item prop="selectedTypePublicHoliday"
                  class="holiday-modal-form-item holiday-modal-control--holiday">
                  <el-select filterable clearable v-model="newHolidayFormModel.selectedTypePublicHoliday"
                    placeholder="Selecciona el tipo de festividad" class="w-100" :value-key="'id'"
                    @change="handleTypePublicHolidayChange"
                    :class="newHolidayFormRules.selectedTypePublicHoliday.error ? 'error-validation' : ''">
                    <el-option v-for="typePublicHoliday in typesPublicHolidays" :key="typePublicHoliday.id"
                      :label="typePublicHoliday.type" :value="typePublicHoliday" />
                  </el-select>
                </el-form-item>
              </div>
            </div>
          </div>

          <div class="row item-modal">
            <div class="col-sm-12">
              <label class="work-center-label w-100">Centro de trabajo</label>
              <el-form-item prop="checkedWorkplaces" class="modal-checkboxes-container"
                :class="newHolidayFormRules.checkedWorkplaces.error ? 'error-validation' : ''">
                <el-checkbox-group v-model="checkedWorkplaces" class="workplace-checkbox-grid" size="small">
                  <el-checkbox-button v-for="workplace in workplaceOptions" :key="workplace.id" :label="workplace"
                    :value="workplace">{{
                      workplace.name
                    }}</el-checkbox-button>
                </el-checkbox-group>

              </el-form-item>
            </div>
          </div>

          <div class="row ">
            <div class="col-sm-12">
              <div class="row tex-center">
                <div class="col-sm-12 d-inline-flex align-items-center">
                  <label>Festividad de:</label>
                </div>
                <div class="col-sm-12">
                  <el-form-item prop="descriptionModal">
                    <el-input type="textarea" v-model="newHolidayFormModel.descriptionModal" class="description-item"
                      :rows="3" :error-validation="newHolidayFormRules.descriptionModal.error" />
                  </el-form-item>
                </div>
              </div>
            </div>
          </div>

          <div class="row item-modal offset-2 holiday-dialog-actions">
            <div class="col-sm-5 col-12">
              <button class="btn btn-block btn_red" type="button" @click="closeModalNewPublicHoliday()">
                Cancelar
              </button>
            </div>
            <div class="col-sm-5 col-12">
              <button class="btn btn_blue" type="button" @click="requestNewPublicHoliday()">
                Aceptar
              </button>
            </div>
          </div>
        </el-form>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
//.table-holidays-calendar {
//  padding-top: 20px;
//
//  table {
//    width: 100%;
//    color: #858796;
//
//    th {
//      border: 1px solid #e3e6f0;
//      padding: 12px 12px 12px 20px;
//      font-size: 20px;
//      text-align: left;
//    }
//
//    tbody {
//
//      tr:nth-child(odd) {
//        td {
//          background-color: rgba(0, 0, 0, .05);
//        }
//      }
//
//      td {
//        border: 1px solid #e3e6f0;
//        padding: 12px;
//        padding-left: 20px;
//        background-color: white;
//        text-align: left;
//
//        .holiday-status-container {
//          display: inline-flex;
//          flex-flow: row nowrap;
//          justify-content: center;
//          align-items: center;
//          width: 100%;
//        }
//
//        .holiday-select {
//          width: 80%;
//        }
//
//        .holiday-button {
//          cursor: pointer;
//          margin-left: 20px;
//        }
//      }
//    }
//  }
//}

.new-holiday-button {
  margin-top: 20px;
  ;
}

.pagination {
  padding: 0px 0px 20px 0px;
}

.table-mobile {
  display: none;
}

.master-mobile-card {
  border-radius: 16px;
}

.master-mobile-card :deep(.el-card__body) {
  position: relative;
  padding: 16px 18px;
}

.master-mobile-card__delete {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 1;
}

.master-mobile-card__title {
  color: $bg-blue;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 10px;
  padding-right: 42px;
  word-break: break-word;
}

.master-mobile-card__item {
  margin-bottom: 10px;
  word-break: break-word;
}

.master-mobile-card__label {
  color: $bg-blue;
  font-weight: 800;
}

.master-mobile-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}

.master-actions-col {
  width: 110px;
  min-width: 110px;
  text-align: center;
}

.item-modal {
  margin-top: 5px;
}

.holiday-modal-fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 12px;
}

.holiday-modal-field {
  align-items: center;
  margin-bottom: 0;
  margin-top: 0;
  min-height: 42px;
}

.holiday-modal-form-item {
  margin-bottom: 0;
  width: 100%;
}

.holiday-modal-form-item :deep(.el-form-item__content) {
  align-items: center;
  line-height: 42px;
  min-height: 42px;
  width: 100%;
}

.holiday-modal-form-item :deep(.el-date-editor.el-input),
.holiday-modal-form-item :deep(.el-select) {
  height: 42px !important;
  width: 100%;
}

.holiday-modal-form-item :deep(.el-input__wrapper),
.holiday-modal-form-item :deep(.el-select__wrapper) {
  align-items: center;
  box-sizing: border-box;
  height: 42px !important;
  min-height: 42px;
}

.holiday-modal-control--year,
.holiday-modal-control--holiday {
  max-width: 250px;
}

.work-center-label {
  display: inline-block;
  margin-bottom: 8px;
}

.modal-checkboxes-container {
  margin-bottom: 10px;
}

.modal-checkboxes-container :deep(.el-form-item__content) {
  line-height: normal;
  width: 100%;
}

.workplace-checkbox-grid {
  display: grid;
  gap: 8px 12px;
  grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
  margin-left: 5px;
  width: 100%;
}

.workplace-checkbox-grid :deep(.el-checkbox-button) {
  margin: 0;
  min-width: 0;
}

.workplace-checkbox-grid :deep(.el-checkbox-button:first-child .el-checkbox-button__inner),
.workplace-checkbox-grid :deep(.el-checkbox-button:last-child .el-checkbox-button__inner),
.workplace-checkbox-grid :deep(.el-checkbox-button__inner) {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe3f1 !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 6px rgba(47, 59, 77, 0.14) !important;
  color: #2f3b4d;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  height: 34px;
  justify-content: center;
  line-height: 1;
  min-width: 84px;
  padding: 0 14px;
  transition: background-color 0.36s ease-out, border-color 0.24s ease-out, box-shadow 0.28s ease-out, color 0.2s ease-out !important;
  white-space: nowrap;
  width: 100%;
}

.workplace-checkbox-grid :deep(.el-checkbox-button__original:checked+.el-checkbox-button__inner),
.workplace-checkbox-grid :deep(.el-checkbox-button.is-checked .el-checkbox-button__inner) {
  background: linear-gradient(180deg, #777cf2 0%, #575ce8 100%);
  border-color: #474ce6 !important;
  box-shadow: 0 0 0 1px rgba(71, 76, 230, 0.72), 0 4px 12px rgba(71, 76, 230, 0.3) !important;
  color: #fff;
}

.workplace-checkbox-grid :deep(.el-checkbox-button__inner:hover) {
  border-color: #409eff !important;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.4), 0 3px 8px rgba(47, 59, 77, 0.14) !important;
  color: #2f3b4d;
}

.workplace-checkbox-grid :deep(.el-checkbox-button.is-focus:not(.is-checked) .el-checkbox-button__inner) {
  border-color: #dfe3f1 !important;
  box-shadow: 0 2px 6px rgba(47, 59, 77, 0.14) !important;
}

.description-item {
  margin-bottom: 0;
}

.error-validation {
  border: 2px solid #F56C6C;
}

.public-holiday-label-modal {
  position: relative;
  bottom: 3px;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

.master-search-actions {
  align-items: flex-end;
}

.master-search-row .form-item {
  margin-bottom: 0;
}

.holiday-new-button {
  color: #fff !important;
  height: 44px !important;
  max-width: 300px;
  min-height: 44px;
  margin-bottom: 0 !important;
  width: 100%;
}

@media (max-width: 767px) {
  .title-text {
    font-size: 2.2em;
  }

  .card-search {
    height: auto;
    padding: 20px 16px;
    background-size: 92px;
    background-position: calc(100% + 16px) 12px;
  }

  .master-search-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .master-search-row .form-item {
    margin-bottom: 12px;
  }

  .master-search-actions {
    align-items: stretch !important;
    margin-bottom: 0;
    margin-top: 10px;
    padding-left: 0;
    padding-right: 0;
    width: 100%;
  }

  .master-search-actions .btn-search {
    margin-left: auto;
    margin-right: auto;
    max-width: 100% !important;
    min-width: 0 !important;
    width: calc(100% - 28px);
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: block;
    padding-right: 14px;
  }

  .holiday-dialog-actions {
    row-gap: 10px;
    margin-left: 0 !important;
  }

  .holiday-dialog-actions .btn {
    width: 100%;
  }

  .holiday-modal-field {
    row-gap: 6px;
  }

  .holiday-modal-control--year,
  .holiday-modal-control--holiday {
    max-width: 100%;
  }

  .workplace-checkbox-grid {
    grid-template-columns: repeat(auto-fit, minmax(128px, 1fr));
  }

  .workplace-checkbox-grid :deep(.el-checkbox-button__inner) {
    width: 100%;
  }
}
</style>

<style lang="scss">
.card-search {
  height: auto !important;
}

// Fix workcenter checkboxes style
form.el-form.newHolidayForm .modal-checkboxes-container .el-form-item__content {
  line-height: normal !important;
}


.el-dialog {
  background: #f3f0fb !important;
}

.el-dialog__body {
  background: #f3f0fb !important;

  .el-dialog__header {
    padding: 0;
  }

  .my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .text-header {
      text-align: left;
      font-size: 4em;
      font-weight: 900;
      letter-spacing: -0.05em;
      color: #f9156f;
    }

    .close-icon {
      width: 8%;
      height: 20%;
    }

    .inter-icon {
      width: 2%;
      height: 2%;
    }

    #asterisk {
      position: absolute;
      vertical-align: super;
      height: auto;
      width: 2.2em;
      fill: #f9156f;
    }

  }

  .my-body {
    margin-right: 5%;
  }

}

.holiday-calendar-dialog .my-body {
  margin-right: 0;
}

@media (max-width: 767px) {
  .holiday-calendar-dialog {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px);
    margin-top: 4vh !important;
  }

  .holiday-calendar-dialog .el-dialog__body {
    min-height: auto !important;
    padding-bottom: 22px;
  }

  .holiday-calendar-dialog .my-header {
    align-items: flex-start;
    gap: 12px;
    padding-right: 10px;
  }

  .holiday-calendar-dialog .text-header {
    font-size: 2em !important;
    line-height: 1.1;
    margin-top: 0.75rem !important;
  }

  .holiday-calendar-dialog .close-icon {
    width: 26px !important;
    min-width: 26px;
    margin-top: 12px;
  }

  .holiday-calendar-dialog .my-body {
    margin-right: 0 !important;
  }

  .holiday-calendar-dialog .modal-checkboxes-container .el-form-item__content {
    width: 100%;
  }

  .holiday-calendar-dialog .workplace-checkbox-grid {
    display: grid;
    gap: 8px 10px;
    grid-template-columns: repeat(auto-fit, minmax(128px, 1fr));
  }
}
</style>
